import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarPlus, Plus, Check } from "lucide-react";
import EmptyState from "../components/EmptyState";
import AktivitaDetail from "../components/AktivitaDetail";
import NovaAktivita from "../components/NovaAktivita";
import ClientForm from "../components/ClientForm";
import { Button } from "../components/ui/kit";
import { SwipeAktivita, SwipeNapoveda, useAktivitaSwipe } from "../components/AktivitaSwipe";
import { useAuth } from "../context/AuthContext";
import { czDate, canEditAktivita } from "../lib/nav";
import { useJeMobil } from "../lib/mobil";
import { fmtKc, fmtTime } from "../lib/format";
import { FAZE_LABEL, FAZE_BARVA, PIPELINE_FAZE } from "../lib/faze";
import api from "../lib/api";
import Segment from "../components/Segment";
import Napoveda from "../components/Napoveda";
import { ikonaTypu } from "../lib/typyAktivit";
import { useObnovaPriNavratu } from "../lib/obnova";

// Barvy pásu pipeline = tečky fází z jediné sady v lib/faze.js.
const PIPELINE = PIPELINE_FAZE.map((f) => [f, FAZE_BARVA[f].dot]);
const MESICE = ["lednu", "únoru", "březnu", "dubnu", "květnu", "červnu",
  "červenci", "srpnu", "září", "říjnu", "listopadu", "prosinci"];
// „25.3." — uzsi nez fmtDate, at se vejde do 42px sloupce u aktivit po terminu.
const dm = (d) => { const x = new Date(d); return `${x.getDate()}.${x.getMonth() + 1}.`; };
const DNY_DLOUHE = ["Neděle", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota"];
/** Nadpis dne v seznamu „Týden" / „Vše": Dnes · Zítra · Čtvrtek 16. 10. */
function nadpisDne(iso) {
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const dnes = new Date(); dnes.setHours(0, 0, 0, 0);
  const rozdil = Math.round((d - dnes) / 86400000);
  const datum = `${d.getDate()}. ${d.getMonth() + 1}.`;
  if (rozdil === 0) return `Dnes · ${datum}`;
  if (rozdil === 1) return `Zítra · ${datum}`;
  return `${DNY_DLOUHE[d.getDay()]} ${datum}`;
}
const OBLAST = {
  OBCHOD: "bg-[#eef4fd] text-[#2c6ecb]",
  SERVIS: "bg-success-bg text-success",
};

function DarkTile({ label, value, note, noteCls = "text-ondark-muted", onClick, testid }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} data-testid={testid}
      className={`bg-sidebar-2 rounded-icon px-4 py-3.5 text-left w-full ${onClick ? "hover:bg-sidebar-3 transition-colors" : ""}`}>
      <div className="flex items-center gap-1.5">
        <span className="label-up text-ondark-muted">{label}</span>
        {onClick && <span className="ml-auto text-[13px] text-ondark-muted">›</span>}
      </div>
      <div className="amount text-ondark text-[19px] md:text-[21px] font-700 mt-1.5 truncate">{value}</div>
      {note && <div className={`text-[11.5px] font-600 mt-1 ${noteCls}`}>{note}</div>}
    </Tag>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [detailId, setDetailId] = useState(null);
  const [editAct, setEditAct] = useState(null);
  const [newAct, setNewAct] = useState(false);
  const [newClient, setNewClient] = useState(false);
  const [rozsah, setRozsah] = useState("dnes");
  const [data, setData] = useState(null);

  const load = () => api.get("/api/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useObnovaPriNavratu(load);   // po návratu do aplikace čerstvá data (odškrtnuté jinde, kolegové)

  const toggle = async (a) => { await api.post(`/api/aktivity/${a.id}/hotovo`); load(); };
  // Na telefonu se řádky tahají (převzato z Proyekt) — jedno kliknutí na
  // odškrtnutí místo detail → tlačítko → dialog.
  const mobil = useJeMobil();
  const swipe = useAktivitaSwipe({ reload: load, onUpravit: setEditAct });

  const oslov = user?.osloveni || user?.name?.split(" ")[0] || "";
  const overdue = data?.aktivity_po_terminu || [];
  const upcoming = data?.nadchazejici_aktivity || [];
  const money = data?.muze_ceny;
  const stav = data?.stav_pripadu || {};

  // Pás pipeline: šířky podle objemu, ne podle počtu — jinak by drobná zakázka
  // vypadala stejně velká jako milionová.
  const bar = useMemo(() => {
    const parts = PIPELINE.map(([f, color]) => ({
      faze: f, color, pocet: stav[f]?.pocet || 0, objem: stav[f]?.objem_kc || 0,
    })).filter((p) => p.pocet > 0);
    const total = parts.reduce((s, p) => s + p.objem, 0);
    return parts.map((p) => ({ ...p, sirka: total ? (p.objem / total) * 100 : 100 / parts.length }));
  }, [stav]);

  const dnes = new Date().toDateString();
  const konecTydne = Date.now() + 7 * 86400000;
  const seznam = useMemo(() => upcoming.filter((a) => {
    if (rozsah === "vse") return true;
    const d = new Date(a.date_start);
    if (rozsah === "dnes") return d.toDateString() === dnes;
    return d.getTime() <= konecTydne;
  }), [upcoming, rozsah, dnes, konecTydne]);
  // Týden a Vše po dnech — u řádku je pak jen čas, den je v nadpisu skupiny.
  const skupiny = useMemo(() => {
    const out = [];
    seznam.forEach((a) => {
      const klic = new Date(a.date_start).toDateString();
      if (!out.length || out[out.length - 1].klic !== klic) out.push({ klic, nadpis: nadpisDne(a.date_start), polozky: [] });
      out[out.length - 1].polozky.push(a);
    });
    return out;
  }, [seznam]);
  const poDnech = rozsah !== "dnes";

  return (
    <div data-testid="dashboard-page">
      {/* hlavička — pozdrav podle návrhu (tykání) + primární akce */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <div className="label-up text-muted mb-1.5 amount">{czDate()}</div>
          <h1 className="text-[22px] md:text-[25px] font-800 text-ink leading-tight tracking-[-.02em]">
            Ahoj {oslov} 👋
          </h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" onClick={() => setNewAct(true)} data-testid="dash-nova-udalost">
            <CalendarPlus size={15} /> <span className="hidden sm:inline">Nová událost</span>
          </Button>
          <Button onClick={() => setNewClient(true)} data-testid="dash-novy-klient">
            <Plus size={15} /> <span className="hidden sm:inline">Nový klient</span>
          </Button>
        </div>
      </div>

      {/* Horní řada upozornění: aktivity po termínu a nedostatky po termínu
          vedle sebe (nedostatky jsou sliby klientovi z terénu, ne úkoly v kalendáři). */}
      {(overdue.length > 0 || data?.nedostatky_po_terminu > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 mb-4">
          {overdue.length > 0 && (
            <div className="card p-4 md:px-[18px]" data-testid="dash-po-terminu">
              <div className="flex items-center gap-2.5">
                <span className="h-2 w-2 rounded-full bg-danger shrink-0" />
                <span className="text-[13.5px] font-700 text-ink">
                  {data.pocet_po_terminu} {data.pocet_po_terminu === 1 ? "aktivita" : data.pocet_po_terminu < 5 ? "aktivity" : "aktivit"} po termínu
                </span>
                <button onClick={() => nav("/ukoly?pohled=SEZNAM&jen=po-terminu")}
                  className="tap md:min-h-0 ml-auto text-[12px] font-600 text-danger">Vyřešit →</button>
              </div>
              <div className="mt-3 flex flex-col gap-2.5">
                {overdue.slice(0, 3).map((a) => (
                  <button key={a.id} onClick={() => setDetailId(a.id)} data-testid={`overdue-${a.id}`}
                    className="flex items-center gap-2.5 text-left w-full">
                    <span className="amount text-[11px] font-600 text-danger w-[42px] shrink-0">{dm(a.date_start)}</span>
                    <span className="flex-1 min-w-0 truncate text-[12.5px] font-600 text-sec">
                      {a.nazev}{a.client_name ? ` — ${a.client_name}` : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {data?.nedostatky_po_terminu > 0 && (
            <div className="card p-4 md:px-[18px]" data-testid="dash-nedostatky">
              <div className="flex items-center gap-2.5">
                <span className="h-2 w-2 rounded-full bg-danger shrink-0" />
                <span className="text-[13.5px] font-700 text-ink">
                  {data.nedostatky_po_terminu}{" "}
                  {data.nedostatky_po_terminu === 1 ? "nedostatek" : data.nedostatky_po_terminu < 5 ? "nedostatky" : "nedostatků"}
                  {" "}po termínu odstranění
                </span>
                <Napoveda>Závady, které jsme klientovi při servisu slíbili opravit a termín už minul.</Napoveda>
                <button onClick={() => nav("/servis?pohled=nedostatky")}
                  className="tap md:min-h-0 ml-auto text-[12px] font-600 text-danger">Zobrazit →</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* tmavý finanční blok */}
      {money && (
        <div className="rounded-[16px] bg-ink text-white p-5 md:p-[26px] mb-4 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-6 lg:gap-7"
          data-testid="finance-block">
          <div className="min-w-0">
            <div className="label-up text-ondark-muted">V pipeline</div>
            <div className="amount font-700 text-[30px] md:text-[42px] tracking-[-.03em] mt-2 truncate">
              {fmtKc(data.v_pipeline_kc || 0)}<span className="text-[16px] md:text-[20px] text-ondark-muted"> Kč</span>
            </div>
            <div className="mt-4 flex h-2.5 rounded-[5px] overflow-hidden bg-sidebar-3" data-testid="pipeline-bar">
              {bar.map((p) => (
                <button key={p.faze} onClick={() => nav(`/obchod?f=faze:${p.faze}`)}
                  title={`${FAZE_LABEL[p.faze]} · ${p.pocet} · ${fmtKc(p.objem)} Kč`}
                  data-testid={`bar-${p.faze}`}
                  style={{ width: `${p.sirka}%`, background: p.color }} />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-3.5 gap-y-2">
              {bar.map((p) => (
                <span key={p.faze} className="flex items-center gap-1.5 text-[11.5px] font-500 text-[#b6bcc4]">
                  <span className="h-[7px] w-[7px] rounded-[2px]" style={{ background: p.color }} />
                  {FAZE_LABEL[p.faze]} {p.pocet}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3.5 content-start">
            <DarkTile label={`Uzavřeno v ${MESICE[new Date().getMonth()]}`}
              value={fmtKc(data.uzavreno_mesic_kc || 0)}
              note={data.uzavreno_mesic_zmena_pct != null
                ? `${data.uzavreno_mesic_zmena_pct > 0 ? "+" : ""}${data.uzavreno_mesic_zmena_pct} %` : null}
              noteCls={data.uzavreno_mesic_zmena_pct >= 0 ? "text-[#4fbf9a]" : "text-danger"}
              testid="tile-uzavreno" />
            <DarkTile label="Průměrná marže"
              value={data.marze_pct != null ? `${String(data.marze_pct).replace(".", ",")} %` : "—"}
              note={`cíl ${data.marze_cil_pct} %`} noteCls="text-[#dc9a2c]" testid="tile-marze" />
            <DarkTile label="Klienti" value={data.pocet_klientu || 0}
              note={data.klienti_pribylo_mesic ? `+${data.klienti_pribylo_mesic} za měsíc` : null}
              noteCls="text-[#4fbf9a]" onClick={() => nav("/klienti")} testid="tile-klienti" />
            <DarkTile label="Obchodní případy" value={data.pripadu || 0}
              note={`${data.pripadu_v_realizaci || 0} v realizaci`}
              onClick={() => nav("/obchod")} testid="tile-pripady" />
          </div>
        </div>
      )}

      {/* Co tě čeká */}
      <div className="card overflow-hidden" data-testid="co-te-ceka">
        <div className="px-[18px] py-[15px] border-b border-line-soft flex items-center gap-3 md:gap-4 flex-wrap">
          <h3 className="text-[14px] font-700 text-ink">Co tě čeká</h3>
          <Segment className="flex gap-1 p-[3px] bg-[#f4f3f0] rounded-pill">
            {[["dnes", "Dnes"], ["tyden", "Týden"], ["vse", "Vše"]].map(([v, l]) => (
              <button data-on={rozsah === v} key={v} onClick={() => setRozsah(v)} data-testid={`rozsah-${v}`}
                className={`text-[12px] font-600 px-3 py-[5px] rounded-pill transition-colors ${
                  rozsah === v ? "text-white" : "text-[#7b7f86] hover:text-ink"}`}>{l}</button>
            ))}
          </Segment>
          <button onClick={() => nav("/ukoly")} className="ml-auto text-[12px] font-600 text-accent-hover"
            data-testid="dash-do-kalendare">Kalendář →</button>
        </div>
        {seznam.length === 0 ? (
          <EmptyState icon="CalendarPlus" title="Nic tě nečeká"
            description={rozsah === "dnes" ? "Na dnešek nemáš naplánovanou žádnou aktivitu." : "V tomhle výhledu nic není."} />
        ) : mobil ? (
          <div className="p-3 space-y-2" data-testid="ceka-swipe">
            <SwipeNapoveda smiUpravit={seznam.some((a) => canEditAktivita(user, a.oblast))} />
            {skupiny.map((g) => (
              <React.Fragment key={g.klic}>
                {poDnech && <div className="t-meta font-700 text-sec pt-2 px-1" data-testid="ceka-den">{g.nadpis}</div>}
                {g.polozky.map((a) => (
                  <SwipeAktivita key={a.id} a={a} cas="cas"
                    onOpen={(x) => setDetailId(x.id)} smiUpravit={canEditAktivita(user, a.oblast)}
                    podradek={(x) => [x.typ, x.client_name, x.obchodni_pripad?.nazev, (x.osoby_names || [])[0]].filter(Boolean).join(" · ")}
                    {...swipe.swipeProps} />
                ))}
              </React.Fragment>
            ))}
          </div>
        ) : skupiny.map((g) => (
          <React.Fragment key={g.klic}>
            {poDnech && (
              <div className="px-[18px] pt-3 pb-1.5 label-up text-muted bg-[#fafaf8] border-b border-line-softer" data-testid="ceka-den">{g.nadpis}</div>
            )}
            {g.polozky.map((a) => {
              const Ikona = ikonaTypu(a.typ);
              return (
                <div key={a.id} onClick={() => setDetailId(a.id)} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") setDetailId(a.id); }}
                  data-testid={`ceka-${a.id}`}
                  className="flex items-center gap-3 md:gap-3.5 px-[18px] py-3 border-b border-line-softer last:border-0 cursor-pointer hover:bg-inputbg">
                  <span className="amount text-[11.5px] font-600 text-[#7b7f86] w-[44px] shrink-0">{fmtTime(a.date_start)}</span>
                  <span className={`h-8 w-8 rounded-full shrink-0 inline-flex items-center justify-center ${OBLAST[a.oblast] || "bg-inputbg text-sec"}`}
                    title={a.typ || ""}>
                    <Ikona size={15} strokeWidth={2} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13.5px] font-600 text-ink truncate">{a.nazev}</span>
                    <span className="block text-[12px] text-muted truncate">
                      {[a.typ, a.client_name, a.obchodni_pripad?.nazev, (a.osoby_names || [])[0]].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  {canEditAktivita(user, a.oblast) && (
                    <button onClick={(e) => { e.stopPropagation(); toggle(a); }} data-testid={`toggle-${a.id}`}
                      title="Označit jako hotové"
                      className="h-7 w-7 rounded-icon border border-line-strong text-muted hover:border-success hover:text-success flex items-center justify-center shrink-0">
                      <Check size={14} strokeWidth={2.4} />
                    </button>
                  )}
                  <span className={`label-up px-2.5 py-1 rounded-pill shrink-0 ${OBLAST[a.oblast] || "bg-inputbg text-sec"}`}>
                    {a.oblast === "SERVIS" ? "Servis" : "Obchod"}
                  </span>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {detailId && (
        <AktivitaDetail id={detailId} onClose={() => setDetailId(null)}
          onEdit={(a) => { setDetailId(null); setEditAct(a); }}
          onChanged={load} />
      )}
      {newAct && <NovaAktivita onClose={() => setNewAct(false)} onSaved={() => { setNewAct(false); load(); }} />}
      {editAct && (
        <NovaAktivita aktivita={editAct} onClose={() => setEditAct(null)}
          onSaved={() => { setEditAct(null); load(); }} />
      )}
      {newClient && <ClientForm onClose={() => setNewClient(false)} onSaved={() => { setNewClient(false); load(); }} />}
      {swipe.supliky}
    </div>
  );
}
